import { pick } from 'lodash';

import { formatToOnlyDigit } from 'commonUtils/formatters/formatToOnlyDigit';

import type { TCustomerData } from 'shared/types/TCustomerService';

import { convertJobTitleField } from 'entities/creditForm/lib/mappersToCreditForm/convertJobTitleField';
import { mapCarAndPropertyAvailability } from 'entities/creditForm/lib/mappersToCreditForm/mapCarAndPropertyAvailability';

import type {
  TAdditionalDataFormFields,
  TContactsFormFields,
  TCreditFormCommonFields,
  TCreditFormEntityReducer,
  TCreditPurposeFormData,
  TPassportFormFields,
  TWorkplaceFormFields,
} from '../../types';

import { convertAddressField } from './convertAddressField';
import { convertCustomerAmountValue } from './convertCustomerAmountValue';
import { convertDictionaryField } from './convertDictionaryField';
import { convertFullNameField } from './convertFullNameField';
import { convertSelectedProductTypeValue } from './convertSelectedProductTypeValue';
import { makeLastWorkExperienceStartMonthFieldValue } from './makeLastWorkExperienceStartMonthFieldValue';
import { makeRegionFieldValue } from './makeRegionFieldValue';
import { mapCustomerAutoFieldsToCreditForm } from './mapCustomerAutoFieldsToCreditForm';

type TBaseMapperInputData = Partial<TCustomerData> & {
  term?: unknown;
};

type TBaseMapperOptions = {
  periodSourceValue: unknown;
  userAgreement: boolean;
  advertisingAgreement: boolean;
  registrationAddressFlat: string;
  residenceAddressFlat: string;
  residenceAddressIsRegistration: boolean;
  okved: string;
};

type TMappedToCreditFormSections = {
  creditPurposeData: TCreditPurposeFormData;
  contactsData: TContactsFormFields;
  passportData: TPassportFormFields;
  workplaceData: TWorkplaceFormFields;
  additionalData: TAdditionalDataFormFields;
};

export const mapBaseDataToCreditForm = (
  state: TCreditFormEntityReducer,
  data: TBaseMapperInputData,
  options: TBaseMapperOptions,
): TMappedToCreditFormSections => {
  const { creditPurposeForm, contactsForm, passportForm, workplaceForm, additionalDataForm, dictionaries } = state;
  const passportNumberWithoutSpaces = formatToOnlyDigit(data.passportNumber);

  const formData: TCreditFormCommonFields = {
    ...mapCustomerAutoFieldsToCreditForm(state, data),
    ...mapCarAndPropertyAvailability(state, data),
    amount: convertCustomerAmountValue(data.amount),
    period:
      (convertDictionaryField(
        options.periodSourceValue,
        'period',
        dictionaries,
      ) as TCreditFormCommonFields['period']) || creditPurposeForm.defaults.period,
    purpose:
      (convertDictionaryField(data.purpose, 'purpose', dictionaries) as TCreditFormCommonFields['purpose']) ||
      creditPurposeForm.defaults.purpose,
    selectedProductType: convertSelectedProductTypeValue(data.selectedProductType),
    region: makeRegionFieldValue(data.region),
    mobilePhone: formatToOnlyDigit(data.mobilePhone),
    email: data.email || '',
    userAgreement: options.userAgreement,
    advertisingAgreement: options.advertisingAgreement,
    fullName: convertFullNameField(data.fullName, data.gender),
    gender: data.gender ?? 'Unknown',
    birthDate: data.birthDate || '',
    birthPlace: data.birthPlace || '',
    passportNumber: passportNumberWithoutSpaces.length === 10 ? passportNumberWithoutSpaces : '',
    passportIssueDate: data.passportIssueDate || '',
    passportIssuedBy: data.passportIssuedBy || '',
    unitCode: data.unitCode || '',
    registrationAddress: convertAddressField(data.registrationAddress),
    registrationAddressFlat: options.registrationAddressFlat,
    registrationDate: data.registrationDate || '',
    residenceAddress: convertAddressField(data.residenceAddress),
    residenceAddressFlat: options.residenceAddressFlat,
    residenceAddressIsRegistration: options.residenceAddressIsRegistration,
    employmentType: convertDictionaryField(
      data.employmentType,
      'employmentType',
      dictionaries,
    ) as TCreditFormCommonFields['employmentType'],
    unemploymentReason: convertDictionaryField(
      data.unemploymentReason,
      'unemploymentReason',
      dictionaries,
    ) as TCreditFormCommonFields['unemploymentReason'],
    anotherUnemploymentReason: data.anotherUnemploymentReason || '',
    monthlySalary: data.monthlySalary ? String(data.monthlySalary) : '',
    areaOfEmployment: convertDictionaryField(
      data.areaOfEmployment,
      'areaOfEmployment',
      dictionaries,
    ) as TCreditFormCommonFields['areaOfEmployment'],
    okved: options.okved,
    employerType: convertDictionaryField(
      data.employerType,
      'employerType',
      dictionaries,
    ) as TCreditFormCommonFields['employerType'],
    workAddress: convertAddressField(data.workAddress),
    workOrganizationName: data.workOrganizationName || '',
    workPhone: formatToOnlyDigit(data.workPhone),
    jobTitle: convertJobTitleField(data),
    lastWorkExperienceStartMonth: makeLastWorkExperienceStartMonthFieldValue(
      data.lastWorkExperienceStartMonth,
      data.lastWorkExperienceStartYear,
    ),
    employerInn: formatToOnlyDigit(data.employerInn),
    education: convertDictionaryField(
      data.education,
      'education',
      dictionaries,
    ) as TCreditFormCommonFields['education'],
    maritalStatus: convertDictionaryField(
      data.maritalStatus,
      'maritalStatus',
      dictionaries,
    ) as TCreditFormCommonFields['maritalStatus'],
    numberOfDependents: convertDictionaryField(
      data.numberOfDependents,
      'numberOfDependents',
      dictionaries,
    ) as TCreditFormCommonFields['numberOfDependents'],
    additionalPhone: formatToOnlyDigit(data.additionalPhone),
    additionalPhoneOwner: convertDictionaryField(
      data.additionalPhoneOwner,
      'additionalPhoneOwner',
      dictionaries,
    ) as TCreditFormCommonFields['additionalPhoneOwner'],
    additionalPhoneOwnerFullName: convertFullNameField(data.additionalPhoneOwnerFullName),
    snils: formatToOnlyDigit(data.snils),
  };

  return {
    creditPurposeData: pick(formData, Object.keys(creditPurposeForm.defaults)) as TCreditPurposeFormData,
    contactsData: pick(formData, Object.keys(contactsForm.defaults)) as TContactsFormFields,
    passportData: pick(formData, Object.keys(passportForm.defaults)) as TPassportFormFields,
    workplaceData: pick(formData, Object.keys(workplaceForm.defaults)) as TWorkplaceFormFields,
    additionalData: pick(formData, Object.keys(additionalDataForm.defaults)) as TAdditionalDataFormFields,
  };
};
